import React from "react";
import {observer} from "mobx-react-lite";
import {Col, Container, Row} from "reactstrap";
import {EventStartAndEndDetails} from "../../../stores/domainStore/pages/models/eventStartAndEndDetails";
import {useTranslation} from "react-i18next";
import DateEdit from "../dateEdit/dateEdit";
import EditField from "../../molecules/editField/editField";
import "./eventStartAndEndEdit.sass";
import {addHours} from "date-fns";
import {utcToZonedTime} from "date-fns-tz";
import Banner from "../../atoms/banner/banner";
import {IconSource} from "../../../shared/enums/iconSource";
import {dateUtils} from "../../../utils/dateUtils";
import {fourHoursInMilliseconds} from "../../../shared/constants";

export interface IEventStartAndEndEditProps {
  value: EventStartAndEndDetails;
  label: string;
  placeholder?: string;
  isMultiline?: boolean;
  isRecurringEvent?: boolean;
  isEditing?: boolean;
  isReleased?: boolean;
  onStartDateChange?: (value: Date | null) => void;
  isEnhancedCalendar?: boolean;
}

// TODO add locale info to datepicker, start here https://stackoverflow.com/questions/54399084/change-locale-in-react-datepicker

const EventStartAndEndEdit: React.FC<IEventStartAndEndEditProps> = ({
  value,
  label,
  isRecurringEvent,
  isEditing,
  isReleased,
  onStartDateChange,
  isEnhancedCalendar
}) => {
  const {t} = useTranslation(["event"]);

  const startDateValidationMessage = () => {
    // Only time component can be changed when editing recurring event.
    if (isRecurringEvent && isEditing) return "";

    if (!value.startDate.date) return t("event:errors.startDate");
    else if (!value.isStartDateValid) return t("event:errors.startDateFuture");
    else if (!value.isStartDateOneHourInFuture)
      return t("event:errors.startDateOneHourInFuture");
    return "";
  };

  const endDateTimeValidationMessage = () => {
    /*
     * TODO: Once we pick up the bug with "Unable to edit Date",
     * Following logics will likely be updated.
     *
     * For now, it's safe to display early time warning, error message will only be triggered when
     * time is not valid during editting the recurring event, as we only deal with time.
     * Based on the existing comment,
     * Only time component can be changed when editing recurring event.
     */
    if (isRecurringEvent && isEditing) {
      return t("event:errors.endTimeAfterStartTime");
    }

    if (!value.endDate.date) return t("event:errors.endDate");
    else if (!value.isEndDateValid) return t("event:errors.endDateFuture");
    else if (!value.isEndDateAfterStartDate)
      return t("event:errors.endDateAfterStart");
    return "";
  };

  const {days, hours, minutes} = dateUtils.timeDifference(
    value.startDate.date,
    value.endDate.date
  );
  const message = t("event:errors.longEventWarningMessage", {
    duration:
      (days > 1 ? days + " days " : days === 1 ? days + " day " : "") +
      (hours > 1 ? hours + " hours " : hours === 1 ? hours + " hour " : "") +
      (minutes > 1
        ? minutes + " minutes "
        : minutes === 1
        ? minutes + " minute "
        : "")
  });

  return (
    <EditField label="">
      <Container fluid className="event-start-and-end-edit">
        <Row>
          <Col>
            <DateEdit
              label={label}
              value={value.startDate}
              dateFormat={"dd/MM/yyyy hh:mm a"}
              validationMessage={startDateValidationMessage()}
              overrideIsRequired={true}
              placeholder={t("event:placeholders.startDate")}
              openToDate={addHours(
                utcToZonedTime(
                  value.startDate.date || new Date(),
                  value.timeZone
                ),
                2
              )}
              preventManualEntry={isRecurringEvent && isEditing}
              showTimeSelectOnly={isRecurringEvent && isEditing}
              timeZone={value.timeZone}
              /* Disable minTime for same-day event time reduction. Changes apply only during unreleased periods.
                 Enabling minTime hides past times */
              disableMinTime={isRecurringEvent && isEditing}
              onChange={() => onStartDateChange?.(value.startDate.date)}
            />
          </Col>

          <div className="date-picker-container">
            {isEnhancedCalendar && (
              <DateEdit
                value={value.endDate}
                label={t("event:labels.end")}
                validationMessage={endDateTimeValidationMessage()}
                overrideIsRequired={true}
                placeholder={t("event:placeholders.endDate")}
                timeZone={value.timeZone}
                preventManualEntry={isRecurringEvent && isEditing}
                showTimeSelectOnly={isRecurringEvent && isEditing}
                disableMinTime={isRecurringEvent && isEditing}
              />
            )}
          </div>
        </Row>
        <div className="banner-area">
          <div className="long-event-banner">
            {value.startDate.date &&
              value.endDate.date &&
              value.endDate.date.getTime() - value.startDate.date.getTime() >
                fourHoursInMilliseconds && (
                <Banner
                  text={message}
                  icon={IconSource.published}
                  enableMarkup={true}
                ></Banner>
              )}
          </div>
          {value.isEventTimeChanged() && isReleased && (
            <Banner
              text={t("event:errors.startDateNonCosmeticWarningMessage")}
              icon={IconSource.info}
            />
          )}
        </div>
      </Container>
    </EditField>
  );
};
export default observer(EventStartAndEndEdit);
